import React, { useState, useEffect, useContext } from "react"
import styled from "styled-components"
import { useForm, Controller } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { BrinkContext } from "../context/BrinkContext"
import { subscribeToNewsletter } from "../context/utils/events/klaviyo"
import { subscribeToNewsletter as pinterestTagSubscribeToNewsletter } from "../context/utils/events/pinterestTag"
import { MEDIA_MIN_MEDIUM, MEDIA_MIN_LARGE } from "../../constants"
import Input from "../ui/Input"
import Button from "../ui/Button"
import Loader from "../ui/Loader"

const Wrapper = styled.div`
  width: 100%;
  color: ${(p) => p.theme.colors.black};
  background-color: ${(p) => p.theme.colors.backgroundDark};
  padding: 0 2.5rem 4rem;
`

const Container = styled.div`
  min-height: 35rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  max-width: 55rem;
  margin: 0 auto;

  ${MEDIA_MIN_LARGE} {
    min-height: 45rem;
  }

  > div {
    width: 100%;
    position: relative;
  }

  label {
    position: absolute;
    height: 0.1rem;
    width: 0.1rem;
    clip: rect(0.1rem, 0.1rem, 0.1rem, 0.1rem);
    border: 0;
    overflow: hidden;
  }

  p {
    margin: 3rem 0;
    text-align: center;
  }
`

const Heading = styled.h3`
  width: 100%;
  font-size: 2.8rem;
  color: ${(p) => p.theme.colors.black};
  text-align: center;
  line-height: 3.2rem;
  margin-bottom: 1rem;

  ${MEDIA_MIN_LARGE} {
    font-size: 2.8rem;
    line-height: 3.2rem;
  }
`

const Form = styled.form`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  justify-content: space-between;
`

const EmailField = styled(Input)`
  width: calc(100% - 7rem);
  margin: 1rem 0;

  input {
    background: none;
    color: ${(p) => p.theme.colors.black};
    border-bottom: 0.1rem solid ${(p) => p.theme.colors.light};
    text-transform: initial;
    padding: 0 2rem;
  }

  ${MEDIA_MIN_MEDIUM} {
    margin-bottom: 0;
  }
`

const SubmitButton = styled(Button)`
  width: 6rem;
  background: ${(p) => p.theme.colors.black};
  color: ${(p) => p.theme.colors.white};

  i {
    font-size: 1.8rem;
  }

  ${MEDIA_MIN_MEDIUM} {
    margin-left: 1rem;
    margin-top: 1rem;
  }
`

const MessageContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  max-width: 100%;
  padding: 1rem 0;
  line-height: 2rem;
  max-width: 40rem;
  margin: 0 auto 1rem;
  letter-spacing: 0.05rem;
  font-size: 1.3rem;
  height: 6rem;

  ${MEDIA_MIN_MEDIUM} {
    padding: 2rem 0;
  }

  i {
    font-size: 2rem;
    margin-right: 0.8rem;
  }

  a {
    display: none;
  }
`

const NewsletterSignup = () => {
  const { t } = useTranslation("translations")
  const { currentStore } = useContext(BrinkContext)
  const { control, errors, handleSubmit } = useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [klaviyoResponse, setKlaviyoResponse] = useState()

  const onSubmit = async (values) => {
    setIsLoading(true)
    const response = await subscribeToNewsletter(
      currentStore.countryCode,
      values.email
    )
    pinterestTagSubscribeToNewsletter()
    setKlaviyoResponse(response)
    setIsLoading(false)
  }

  useEffect(() => {}, [errors.email])

  return (
    <Wrapper>
      {isLoading && <Loader isLoading={isLoading} />}
      <Container>
        <div>
          <Heading>{t("Get exclusive offers!")}</Heading>
          <p>
            {t(
              "Enter your e-mail address below to get our newsletter and offers"
            )}
          </p>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              as={EmailField}
              placeholder={t("Your e-mail")}
              label={t("Your e-mail")}
              control={control}
              name="email"
              type="email"
              rules={{
                required: t("This is a required field"),
                pattern: {
                  value:
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  message: t("Please supply a valid e-mail address")
                }
              }}
            />

            <SubmitButton
              aria-label={t("Receive our newsletter")}
              title={t("Receive our newsletter")}
            >
              {t("Join")}
            </SubmitButton>
          </Form>
          <MessageContainer>
            {Object.keys(errors).length !== 0 ? (
              <>
                <i className="fal fa-exclamation-circle"></i>
                {errors.email.message}
              </>
            ) : (
              klaviyoResponse &&
              klaviyoResponse.data && (
                <>
                  {klaviyoResponse.data.is_subscribed ? (
                    <>
                      <i className="fal fa-exclamation-circle"></i>{" "}
                      {t("This email address has already subscribed")}
                    </>
                  ) : klaviyoResponse.success ? (
                    <>
                      <i className="fal fa-check-circle"></i>{" "}
                      {t("Thank you for subscribing!")}
                    </>
                  ) : (
                    <>
                      <i className="fal fa-exclamation-circle"></i>{" "}
                      {t("An error occured, please try again later.")}
                    </>
                  )}
                </>
              )
            )}
          </MessageContainer>
        </div>
      </Container>
    </Wrapper>
  )
}

export default NewsletterSignup
